import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RiMenu2Line, RiShieldUserLine, RiUser3Line } from "react-icons/ri";
import { FaSignOutAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import { useNavigationContext } from "../../../context/navigation/navigationContext";

/**
 * UI-only refinement of the existing NavBar.
 * - Logic (state, handlers, navigation, contexts) remains unchanged.
 * - Visuals modernized: elevated header, improved spacing/typography,
 *   avatar with initials, role badge, refined buttons, tasteful gradients.
 */
const NavBar = (props) => {
  const { showSideBar, setShowSideBar } = useNavigationContext();
  const {
    logOut,
    setSelectedMenu,
    sysAdmin: { first_name, last_name, role },
  } = useAdminContext();

  const navigate = useNavigate();
  const prettyRole = String(role)?.toLowerCase()?.replaceAll("_", " ");

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const confirmLogout = () => {
    navigate("/login");
    setSelectedMenu(0);
    logOut();
  };

  // Derive user presentation (UI only)
  const fullName = [first_name, last_name].filter(Boolean).join(" ");
  const initials = (first_name?.[0] || "").toUpperCase() + (last_name?.[0] || "").toUpperCase();

  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 shadow-[0_6px_24px_rgba(16,24,40,0.06)]">
      <div className="px-3 sm:px-4">
        <div className="flex h-14 sm:h-[60px] items-center justify-between">
          {/* Left: menu + title */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="Open sidebar"
              onClick={() => setShowSideBar(!showSideBar)}
              className="xl:hidden inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <RiMenu2Line className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2">
              <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-gradient-to-tr from-emerald-500 via-amber-500 to-purple-500" />
              <h1 className="text-[13px] sm:text-sm md:text-base font-semibold tracking-tight text-gray-900 uppercase">
                {props.title}
              </h1>
            </div>
          </div>

          {/* Right: user + sign out */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* User chip */}
            <div className="hidden sm:flex items-center gap-3">
              <div className="relative">
                <div className="flex h-9 w-9 items-center justify-center rounded-full ring-2 ring-white shadow-inner bg-gradient-to-br from-gray-50 to-white border border-gray-200">
                  <span className="text-[11px] font-semibold text-gray-700 select-none">
                    {initials || <RiUser3Line className="h-4 w-4" />}
                  </span>
                </div>
                {/* online dot (decorative) */}
                <span className="absolute -bottom-0.5 -right-0.5 inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" aria-hidden />
              </div>

              <div className="flex flex-col leading-tight">
                <span className="text-xs text-gray-800 font-medium truncate max-w-[12rem]" title={fullName}>
                  {fullName}
                </span>
                <span className="inline-flex items-center gap-1 self-start rounded-full border border-gray-200 bg-white px-2 py-[2px] text-[10px] font-semibold capitalize text-gray-700">
                  <RiShieldUserLine className="h-3 w-3 text-emerald-600" aria-hidden />
                  {prettyRole || "member"}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/90 px-3.5 py-2 text-[11px] sm:text-xs font-semibold text-red-700 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
            >
              <span className="uppercase tracking-wide">Sign Out</span>
              <span className="relative flex h-5 w-5 items-center justify-center rounded-md border border-red-200 bg-white group-hover:translate-x-0.5 transition">
                <FaSignOutAlt className="h-3.5 w-3.5" />
                <span className="sr-only">Log out</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Subtle accent line */}
      <div className="h-[3px] w-full bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500/90 opacity-25" />

      {/* Logout confirmation modal (PORTALED) */}
      <LogoutConfirmModal
        open={showLogoutModal}
        name={fullName}
        role={prettyRole}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
      />
    </header>
  );
};

const LogoutConfirmModal = ({ open, onCancel, onConfirm, name, role }) => {
  if (!open) return null;
  return createPortal(
    <OverlayUI open={open} onCancel={onCancel} onConfirm={onConfirm} name={name} role={role} />,
    document.body
  );
};

const OverlayUI = ({ onCancel, onConfirm, name, role }) => {
  const cancelRef = useRef(null);

  // Esc to close + focus + prevent body scroll (UI behavior unchanged)
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel?.();
    window.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const t = requestAnimationFrame(() => cancelRef.current?.focus());

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(t);
    };
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[99999999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-title"
      aria-describedby="logout-desc"
      onClick={onCancel}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

      {/* Centered dialog */}
      <div className="relative z-10 flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Ambient gradient ring */}
          <div aria-hidden className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-emerald-200/45 via-amber-200/40 to-purple-200/45 blur-xl" />

          {/* Header */}
          <div className="relative flex items-start justify-between px-4 pt-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200/60 bg-amber-50/60 px-2.5 py-1 text-[10px] font-semibold text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
              Session
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onCancel}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="relative px-6 pb-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-100">
              <FaSignOutAlt className="h-6 w-6" aria-hidden="true" />
            </div>

            <h3 id="logout-title" className="text-lg font-semibold tracking-tight text-gray-900">
              Sign out?
            </h3>
            <p id="logout-desc" className="mt-2 text-sm text-gray-700">
              You’re about to log out
              {name ? (
                <>
                  {" "}
                  as <span className="font-medium">{name}</span>
                  {role ? (
                    <>
                      {" "}(<span className="capitalize">{role}</span>)
                    </>
                  ) : null}
                  .
                </>
              ) : (
                "."
              )}{" "}
              Any unsaved changes may be lost.
            </p>

            {/* Helper line */}
            <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-[11px] text-gray-700">
              <kbd className="rounded-md border border-gray-300 bg-white px-1.5 py-[1px] text-[10px] font-semibold text-gray-700 shadow-sm">Esc</kbd>
              to cancel •
              <span className="inline-flex items-center gap-1">
                <FaSignOutAlt className="h-3.5 w-3.5" />
                to confirm below
              </span>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                ref={cancelRef}
                onClick={onCancel}
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                <span className="absolute inset-0 -z-10 bg-gradient-to-r from-red-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
                <FaSignOutAlt className="mr-2 h-4 w-4" />
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavBar;
